import type { Application } from "pixi.js";
import type { PixiPresentationTicker, PresentationTaskRunner, ViewportStore } from "@slot/presentation";
import type { AutoSpinSnapshot } from "../autospin/AutoSpinTypes";
import type { ReplayStatus } from "../boot/flows/replayRound";
import type { RestoreStatus } from "../boot/flows/restorePendingRound";
import type { GameRuntime } from "../composition/GameRuntime";
import type { SpinCoordinator } from "../spin/SpinCoordinator";
import type { SpinSpeedMode } from "../spin/SpinCoordinatorTypes";
import type { SpinRejectionCode } from "./RoundFaultPort";

/**
 * `window.__slot` from the first frame: the dev-mode handle automated checks drive the game through
 * (Playwright: wait for ready, step the clock, screenshot). Installed only when the game boots in dev mode.
 */
export interface SlotBootDebugHook {
  readonly version: string;
  readonly app: Application;
  readonly viewport: ViewportStore;
  /** True once the first frame with real content has rendered. */
  isReady: boolean;
  /** Resolves when `isReady` flips true. */
  readonly ready: Promise<void>;
  /** Called by the game once its first content frame is on screen. */
  markReady(): void;
  /** Renders one frame synchronously (for deterministic screenshots). */
  renderFrame(): void;
}

/**
 * The whole hook, completed by `installRuntimeDebugHook` once the runtime exists (before `isReady`): the round-driving
 * surface the e2e suites use, the same for every title. A title adds its own members through
 * `GameDevDefinition.installHook`. Until 2026-10-01 the game wrote these members itself (slot-cannon's
 * `installGameDebugHook`), so a second title would have copied them (decision 38).
 */
export interface SlotDebugHook extends SlotBootDebugHook {
  readonly runtime: GameRuntime;
  readonly spinCoordinator: SpinCoordinator;
  readonly runner: PresentationTaskRunner;
  readonly presentationTicker: PixiPresentationTicker;
  /** Pin every following base spin to one fixture: a playlist of one (offline mode only). */
  loadFixture(id: string): void;
  /** Play these fixtures on the following base spins, in order, looping (offline mode only): the dev menu's playlist. */
  loadFixtures(ids: string[]): void;
  /** The playlist the next base spins play; empty while the catalog's round robin plays, and on a backend. */
  getPlaylist(): string[];
  /** Back to the catalog's round robin. */
  clearFixture(): void;
  /** The game's selectable fixture ids (offline); empty on a backend. */
  listFixtures(): string[];
  /** Request a spin (as the spin button would). */
  spin(): Promise<void>;
  slam(): void;
  setSpeedMode(mode: SpinSpeedMode): void;
  /** Resolves once the coordinator is idle AND the runner has no active tasks. */
  waitForIdle(timeoutMs?: number): Promise<void>;
  getActiveTaskCount(): number;
  getActiveScopes(): Record<string, number>;
  /** The coordinator phase (for polling from the outside). */
  getPhase(): string;
  /** Active interaction-block reasons (a modal panel holding the round: `big_win`, `feature_trigger_panel`, …). */
  getInteractionBlocks(): string[];
  /** Open Pixi overlays over the game — the DOM bar family hides while it is above zero. */
  getCanvasOverlayCount(): number;
  /** True while a feature session (free spins) is running. */
  isFeatureActive(): boolean;
  /** The active feature mode id (a purchased per-spin mode such as `bonus_hunt_mode`), or null. */
  getFeatureMode(): string | null;
  /** True once the intro gesture (or the dev-build intro skip) unlocked the WebAudio context. */
  isAudioUnlocked(): boolean;
  /** "Tap anywhere to continue": the key press every modal panel accepts (Enter — never Space, which spins). */
  pressToContinue(): void;
  /** True while a round waits for the player's Spin (decision 55): the Spin button's spin face, or Space, goes on. */
  isAwaitingSpin(): boolean;
  /** The bet and its lines (decision 56): what a spin costs, a line's bet, the count, the counts offered, whether it can change. */
  getBetLines(): { betMinor: string; lineBetMinor: string; lines: number; options: number[]; canChange: boolean };
  /** True when the settled round offers a gamble and the coordinator is idle (the control bar's gamble button state). */
  canGamble(): boolean;
  /** Enter the offered gamble (as the control bar's gamble button would). */
  enterGamble(): Promise<void>;
  /** Tap the first SHOWN Pixi node labelled `label` (a button: `RedButton`, `SkipButton`, `BuyButton`…; the DOM chrome is driven through Playwright locators). False when none is shown. */
  tapNode(label: string): boolean;
  /** True when a display object labelled `label` is visible through every ancestor. */
  isShown(label: string): boolean;
  /**
   * The string of the first shown `Text` or `SpriteText` labelled `label`; null when none. A sprite text answers the
   * string it was given: a character its font has no glyph for, or one past `maxCharacters`, is in it but not drawn.
   */
  getText(label: string): string | null;
  /** Canvas-space (CSS px) bounds of the first shown node labelled `label`, for real pointer input. */
  getNodeBounds(label: string): { x: number; y: number; width: number; height: number } | null;
  /** FNV-1a hash of the first SHOWN node with `label` rendered alone (no background bleed) — for comparing two boots (branding). */
  getNodeHash(label: string): string | null;
  /** A shown button's `interactable` flag; null when no such button is shown. */
  isInteractable(label: string): boolean | null;
  getAutoSpin(): AutoSpinSnapshot;
  getSpeedMode(): string;
  getAudioSettings(): { masterVolume: number; musicEnabled: boolean };
  isHapticsEnabled(): boolean;
  /**
   * Every call the game made on the audio service since the last `clearAudioLogs()`, in order:
   * `emit:<event>→<sound>`, `play:<id>`, `sfx_loop_start:<id>`, `sfx_loop_stop:<id>`, `music:<id>`,
   * `music_intro:<intro>→<loop>`, `feature_state:<id|null>`, `duck:<amount>`, `unduck`, `slam`, `speed:<mode>` …
   */
  getAudioLog(): string[];
  /** Every haptic pattern the game played since the last `clearAudioLogs()` (`soft`, `success`, `failure`, `custom`). */
  getHapticsLog(): string[];
  clearAudioLogs(): void;
  /** The audio engine's dev snapshot: context state, unlocked, active sources, loop ids, ducking, buses, drop counters. */
  getAudioDebugSnapshot(): Record<string, unknown>;
  /** `?replay=<id>` progress: `fetching` → `playing` → `complete`, or `fetch_failed` / `playback_failed`; null outside replay mode. */
  getReplayStatus(): ReplayStatus | null;
  /**
   * Pending-round restore progress (`initialSettings.pendingRoundId`, `?pending=<id>` in dev): `fetching` →
   * `offered` (the modal) → `declined` | `restoring` → `restored` | `restore_failed`; `fetch_failed` skips the
   * offer; null when no round was pending.
   */
  getRestoreStatus(): RestoreStatus | null;
  /** The round of the last reply that arrived; a request whose reply was lost leaves the previous one. */
  getLastRoundId(): string | null;
  /** The balance store's current amount (minor units) — the spin deduction, then the settlement response. */
  getBalanceMinor(): string;
  /** The next spin request is refused by the "server": low balance by default, or another spin in flight (offline only). */
  rejectNextSpin(code?: SpinRejectionCode): void;
  /** The next spin is served and its reply lost: the "Connection Lost" path (offline only). */
  failNextSpin(): void;
  /**
   * The next `count` settlement requests throw, default 1 (offline only): the round stays pending for
   * the next boot. A winning feature round retries its settlement once, so it needs `2` to fail.
   */
  failNextSettlement(count?: number): void;
  /** Extra latency on every following request, ms (offline only). */
  setSimulatedLatency(ms: number): void;
}

declare global {
  interface Window {
    /** The boot half from the first frame; the whole `SlotDebugHook` once the runtime exists. */
    __slot?: SlotBootDebugHook;
  }
}

/** The hook's boot half, from the first frame of a dev-mode boot. `installRuntimeDebugHook` completes it. */
export function installDebugHook(input: { version: string; app: Application; viewport: ViewportStore }): SlotBootDebugHook {
  let resolveReady: () => void = () => {};
  const ready = new Promise<void>((resolve) => {
    resolveReady = resolve;
  });
  const hook: SlotBootDebugHook = {
    version: input.version,
    app: input.app,
    viewport: input.viewport,
    isReady: false,
    ready,
    markReady(): void {
      if (hook.isReady) return;
      hook.isReady = true;
      resolveReady();
    },
    renderFrame(): void {
      input.app.render();
    },
  };
  window.__slot = hook;
  return hook;
}
